import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Wrench, Moon } from 'lucide-react';
import { API } from '../utils';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', icon: Globe, color: 'emerald', description: 'Site is live and accepting orders' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'amber', description: 'Shows "Under Maintenance" page' },
  { value: 'closed', label: 'Closed for Today', icon: Moon, color: 'indigo', description: 'Shows "Closed for Today" page' },
];

const SiteStatusControl = ({ token }) => {
  const [currentStatus, setCurrentStatus] = useState('open');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/site-status`);
        setCurrentStatus(res.data.status || 'open');
      } catch {
        setCurrentStatus('open');
      }
    };
    fetchStatus();
  }, []);

  const updateStatus = async (newStatus) => {
    if (newStatus === currentStatus) return;
    setLoading(true);
    try {
      await axios.put(`${API}/admin/site-status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentStatus(newStatus);
      const label = STATUS_OPTIONS.find(o => o.value === newStatus)?.label;
      toast.success(`Site status: ${label}`);
    } catch (err) {
      toast.error('Failed to update site status');
    } finally {
      setLoading(false);
    }
  };

  const colorMap = {
    emerald: {
      activeBg: 'bg-emerald-50',
      activeBorder: 'border-emerald-400',
      activeText: 'text-emerald-700',
      activeIcon: 'text-emerald-500',
      dot: 'bg-emerald-500',
    },
    amber: {
      activeBg: 'bg-amber-50',
      activeBorder: 'border-amber-400',
      activeText: 'text-amber-700',
      activeIcon: 'text-amber-500',
      dot: 'bg-amber-500',
    },
    indigo: {
      activeBg: 'bg-indigo-50',
      activeBorder: 'border-indigo-400',
      activeText: 'text-indigo-700',
      activeIcon: 'text-indigo-500',
      dot: 'bg-indigo-500',
    },
  };

  return (
    <div className="bg-white border rounded-xl p-4 mb-6" data-testid="site-status-control">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-2 h-2 rounded-full ${colorMap[STATUS_OPTIONS.find(o => o.value === currentStatus)?.color]?.dot}`} />
        <span className="text-sm font-semibold text-gray-700">Site Status</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {STATUS_OPTIONS.map((opt) => {
          const isActive = currentStatus === opt.value;
          const colors = colorMap[opt.color];
          const Icon = opt.icon;
          return (
            <button
              key={opt.value}
              onClick={() => updateStatus(opt.value)}
              disabled={loading}
              data-testid={`site-status-${opt.value}`}
              className={`
                relative flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all duration-200
                ${isActive
                  ? `${colors.activeBg} ${colors.activeBorder} ${colors.activeText}`
                  : 'bg-gray-50 border-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                }
                ${loading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              <Icon className={`w-5 h-5 ${isActive ? colors.activeIcon : ''}`} />
              <span className="text-xs font-semibold">{opt.label}</span>
              {isActive && (
                <span className="text-[10px] font-normal opacity-70 text-center leading-tight hidden sm:block">
                  {opt.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SiteStatusControl;
